import React from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useNav4Theme } from '@jira/navigation-apps__atlassian-navigation-theme/src/main.tsx';
import { JiraAtlassianNavigationSkeleton as TopNavigationSkeleton } from '@jira/navigation-apps__atlassian-navigation-skeleton/src/JiraAtlassianNavigationSkeleton.tsx';
import { AtlassianNavigationStart } from '@jira/navigation-apps__atlassian-navigation/src/controllers/atlassian-navigation-start/index.tsx';
import { PAGE_LAYOUT_OFFSET_TOP } from '@jira/platform__common-constants/src/page-layout.tsx';
import { useTopNavState } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/top-nav-controller/index.tsx';
import { TopNavigation } from '@jira/design-system__navigation-system/src/PageLayout.tsx';
import { ReportSSRFailOnUse } from '@jira/ssr__rendering-guard/src/index.tsx';
import { PlaceholderWithNativeSuspense } from '@atlassian/jira-placeholder/src/index.tsx';
import { AsyncAtlassianNavigation } from './async-atlassian-navigation/index.tsx';

const LegacyPageNavigationStyles = () => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
	<style
		// oxlint-disable-next-line react/no-danger
		dangerouslySetInnerHTML={{
			__html: `
                    #page-body { width: 100vw; max-width: 100%; }
                    #cloneissueprogressform { margin-top: 24px; }
                    [data-testid="Content"] {margin-top: calc(${PAGE_LAYOUT_OFFSET_TOP})!important;}
                    #view-projects-content { margin-top: calc(-1 * ${PAGE_LAYOUT_OFFSET_TOP}); } /* admin project dir hack */
                `,
		}}
	/>
);

/**
 * This test ID is used in pollinator tests. Do not change lightly.
 * It's only added once the component is rendered client-side.
 */
const TEST_ID = 'page-layout.top-nav';

/**
 * Implements a responsive top navigation bar that renders based on application state.
 * It ensures seamless navigation experience across different sections of the application
 * while handling errors gracefully and tracking performance.
 */
export function TopNavEntry(): React.JSX.Element | null {
	const { shouldRender } = useTopNavState();

	const theme = useNav4Theme();

	if (!shouldRender) {
		return null;
	}

	return (
		<>
			<LegacyPageNavigationStyles />
			<TopNavigation testId={TEST_ID} theme={theme}>
				{!__SERVER__ && <AtlassianNavigationStart />}
				<JSErrorBoundary
					id="navigation.atlassian-navigation"
					packageName="jiraPageContainerV2"
					fallback="flag"
				>
					<PlaceholderWithNativeSuspense
						name="top_navigation_skeleton"
						fallback={
							<>
								<TopNavigationSkeleton />
								<ReportSSRFailOnUse pageId="navigation" fallbackId="TopNav" displayErrorInStaging />
							</>
						}
					>
						<AsyncAtlassianNavigation />
					</PlaceholderWithNativeSuspense>
				</JSErrorBoundary>
			</TopNavigation>
		</>
	);
}
